import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Film, Link2, User, FileText, CheckCircle } from 'lucide-react';

// Field-by-field walkthrough of the Submit form, distinct from Guidelines.js
// (content policy / eligibility) and FAQ.js (process questions) — this is
// specifically "which box do I fill in and what goes in it."
const SECTIONS = [
  {
    icon: <Film size={16} />,
    title: 'Film Details',
    fields: [
      ['Film Title', 'The full, official title of your film — exactly as you want it to appear if published.'],
      ['Genre', 'Pick the closest match from the list. If your film blends genres, choose the one that best describes the overall tone.'],
      ['Language', 'The primary spoken language of the film. If it\'s a silent film, pick the language of any on-screen text/subtitles.'],
      ['Duration', 'Your film\'s runtime, e.g. "18 minutes" or "22 mins". Be accurate — reviewers plan their watch time around this.'],
      ['Synopsis', '3–5 sentences describing what your film is about. This is the first thing reviewers read before watching, so make it count.'],
      ['Notes to Reviewers (optional)', 'Anything specific reviewers should know before watching — e.g. "audio starts at 2:30", "shot on a phone", content warnings, etc. Skip if not needed.'],
    ],
  },
  {
    icon: <Link2 size={16} />,
    title: 'Film Links',
    fields: [
      ['Film Link', 'A shareable link to your film on Google Drive, YouTube, or Vimeo — no other hosts are accepted, and no direct file uploads. Set sharing to "Anyone with the link can view" (Google Drive) or Unlisted (YouTube/Vimeo), otherwise reviewers won\'t be able to open it.'],
      ['Film Link Password (optional)', 'Only fill this in if your link itself is password-protected (e.g. a private Vimeo password). Leave blank otherwise.'],
      ['Trailer Link (optional)', 'A shorter preview clip, if you have one. Not required.'],
    ],
  },
  {
    icon: <User size={16} />,
    title: 'Director Details',
    fields: [
      ['Director Name', 'Full legal name of the director. Used only for our records and the licensing agreement if your film is approved.'],
      ['Phone Number', 'A number we can reach you on. Never shared with reviewers — the review process is fully anonymous.'],
      ['Contact Email', 'Where we\'ll send status updates, feedback, and (if approved) the licensing agreement. Double-check this is correct — it\'s how we reach you.'],
    ],
  },
];

export default function HowToSubmit() {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <Navbar />
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 24px' }}>
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--pink)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10 }}>Help</div>
          <h1 style={{ fontWeight: 900, fontSize: 32, color: 'var(--text)', marginBottom: 8, letterSpacing: '-0.02em' }}>How to submit your film</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, fontWeight: 400, lineHeight: 1.6 }}>
            A field-by-field guide to the submission form. Every field marked <span style={{ color: 'var(--pink)', fontWeight: 700 }}>*</span> on the actual form is required — you won't be able to submit until it's filled in.
          </p>
        </div>

        <div className="card" style={{ padding: '14px 18px', marginBottom: 28, display: 'flex', alignItems: 'center', gap: 10, background: 'var(--pink-glow)', border: '1.5px solid var(--pink-glow-strong)' }}>
          <CheckCircle size={16} color="var(--pink)" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600 }}>
            Quick version: fill in every field, review your answers on the next screen, then confirm. You can always edit before it's opened by our team.
          </span>
        </div>

        {SECTIONS.map(section => (
          <div key={section.title} style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, paddingBottom: 12, borderBottom: '1.5px solid var(--border)' }}>
              <div style={{ color: 'var(--pink)' }}>{section.icon}</div>
              <h3 style={{ fontWeight: 800, fontSize: 15, color: 'var(--text)', letterSpacing: '-0.01em' }}>{section.title}</h3>
            </div>
            {section.fields.map(([label, desc]) => (
              <div key={label} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, fontWeight: 400 }}>{desc}</div>
              </div>
            ))}
          </div>
        ))}

        <div className="card" style={{ padding: 20, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <FileText size={16} color="var(--pink)" />
            <h3 style={{ fontWeight: 800, fontSize: 15, color: 'var(--text)' }}>Still have questions?</h3>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 14 }}>
            For eligibility rules and content policy, see the Submission Guidelines. For questions about what happens after you submit, check the FAQ.
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => navigate('/guidelines')} className="btn-ghost" style={{ flex: 1 }}>Guidelines</button>
            <button onClick={() => navigate('/faq')} className="btn-ghost" style={{ flex: 1 }}>FAQ</button>
          </div>
        </div>

        <button onClick={() => navigate('/submit')} className="btn-primary" style={{ width: '100%', padding: '13px' }}>
          Start Your Submission →
        </button>
      </div>
    </div>
  );
}
